import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Play, RotateCcw, Square, Terminal } from 'lucide-react';
import {
  SERVICE_CATALOG,
  syntheticLogLines,
  type OpsEnvironment,
  type OpsRuntime,
  type ServiceId,
} from './opsConsoleData';
import { PrimaryButton, StatusPill } from './components';

type ServiceStatus = 'stopped' | 'starting' | 'running' | 'failed';

interface ServiceRuntimeState {
  status: ServiceStatus;
  logs: string[];
  logsOpen: boolean;
}

function emptyStates(): Record<ServiceId, ServiceRuntimeState> {
  const out = {} as Record<ServiceId, ServiceRuntimeState>;
  for (const s of SERVICE_CATALOG) {
    out[s.id] = { status: 'stopped', logs: [], logsOpen: false };
  }
  return out;
}

function profileKey(env: OpsEnvironment, runtime: OpsRuntime) {
  return `${env}::${runtime}`;
}

export default function ServicesPanel({
  env,
  runtime,
}: {
  env: OpsEnvironment;
  runtime: OpsRuntime;
}) {
  const key = profileKey(env, runtime);
  const [states, setStates] = useState<Record<ServiceId, ServiceRuntimeState>>(emptyStates);
  const timersRef = useRef<Record<string, number>>({});
  const healthRef = useRef<Record<string, number>>({});

  // Reset synthetic runtime when profile changes
  useEffect(() => {
    Object.values(timersRef.current).forEach((id) => window.clearTimeout(id));
    Object.values(healthRef.current).forEach((id) => window.clearInterval(id));
    timersRef.current = {};
    healthRef.current = {};
    setStates(emptyStates());
  }, [key]);

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((id) => window.clearTimeout(id));
      Object.values(healthRef.current).forEach((id) => window.clearInterval(id));
    };
  }, []);

  const appendLogs = (id: ServiceId, lines: string[]) => {
    setStates((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        logs: [...prev[id].logs, ...lines].slice(-80),
      },
    }));
  };

  const clearHealth = (id: ServiceId) => {
    if (healthRef.current[id]) {
      window.clearInterval(healthRef.current[id]);
      delete healthRef.current[id];
    }
  };

  const startService = (id: ServiceId) => {
    const svc = SERVICE_CATALOG.find((s) => s.id === id)!;
    if (timersRef.current[id]) window.clearTimeout(timersRef.current[id]);
    clearHealth(id);

    setStates((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        status: 'starting',
        logsOpen: true,
        logs: [
          ...prev[id].logs,
          `${new Date().toISOString().replace('T', ' ').slice(0, 23)} [INFO]  Start requested (${runtime})`,
        ].slice(-80),
      },
    }));

    const delay = 800 + Math.floor(Math.random() * 700);
    timersRef.current[id] = window.setTimeout(() => {
      appendLogs(id, syntheticLogLines(svc, 'start'));
      setStates((prev) => ({
        ...prev,
        [id]: { ...prev[id], status: 'running' },
      }));
      healthRef.current[id] = window.setInterval(() => {
        appendLogs(id, syntheticLogLines(svc, 'health'));
      }, 8000);
    }, delay);
  };

  const stopService = (id: ServiceId) => {
    const svc = SERVICE_CATALOG.find((s) => s.id === id)!;
    if (timersRef.current[id]) {
      window.clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }
    clearHealth(id);
    appendLogs(id, syntheticLogLines(svc, 'stop'));
    setStates((prev) => ({
      ...prev,
      [id]: { ...prev[id], status: 'stopped' },
    }));
  };

  const restartService = (id: ServiceId) => {
    stopService(id);
    window.setTimeout(() => startService(id), 400);
  };

  const startAll = () => {
    SERVICE_CATALOG.forEach((s, i) => {
      window.setTimeout(() => startService(s.id), i * 120);
    });
  };

  const stopAll = () => {
    SERVICE_CATALOG.forEach((s) => stopService(s.id));
  };

  const counts = useMemo(() => {
    const vals = Object.values(states);
    return {
      running: vals.filter((v) => v.status === 'running').length,
      starting: vals.filter((v) => v.status === 'starting').length,
      stopped: vals.filter((v) => v.status === 'stopped').length,
    };
  }, [states]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          {counts.running} running · {counts.starting} starting · {counts.stopped} stopped
          <span className="ml-2 text-slate-400">(synthetic · {env} / {runtime})</span>
        </p>
        <div className="flex items-center gap-1.5">
          <PrimaryButton onClick={startAll}>
            <Play size={12} /> Start all
          </PrimaryButton>
          <PrimaryButton variant="danger" onClick={stopAll}>
            <Square size={12} /> Stop all
          </PrimaryButton>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {SERVICE_CATALOG.map((svc) => {
          const st = states[svc.id];
          return (
            <div key={svc.id} className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-3 py-2.5 flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <h4 className="text-xs font-semibold text-slate-900 truncate">{svc.name}</h4>
                    <StatusPill status={st.status} />
                  </div>
                  <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                    {svc.id} · :{svc.defaultPort}
                  </p>
                </div>
              </div>

              <div className="px-3 pb-2.5 flex flex-wrap gap-1.5">
                <PrimaryButton
                  onClick={() => startService(svc.id)}
                  disabled={st.status === 'starting' || st.status === 'running'}
                >
                  <Play size={12} /> Start
                </PrimaryButton>
                <PrimaryButton
                  variant="danger"
                  onClick={() => stopService(svc.id)}
                  disabled={st.status === 'stopped'}
                >
                  <Square size={12} /> Stop
                </PrimaryButton>
                <PrimaryButton
                  variant="secondary"
                  onClick={() => restartService(svc.id)}
                  disabled={st.status === 'starting'}
                >
                  <RotateCcw size={12} /> Restart
                </PrimaryButton>
                <PrimaryButton
                  variant="ghost"
                  onClick={() =>
                    setStates((prev) => ({
                      ...prev,
                      [svc.id]: { ...prev[svc.id], logsOpen: !prev[svc.id].logsOpen },
                    }))
                  }
                >
                  <Terminal size={12} />
                  Logs
                  {st.logsOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                </PrimaryButton>
              </div>

              {st.logsOpen && (
                <div className="border-t border-slate-100 bg-slate-950 px-3 py-2 max-h-40 overflow-y-auto">
                  {st.logs.length === 0 ? (
                    <p className="text-[10px] font-mono text-slate-500">No log output yet.</p>
                  ) : (
                    <pre className="text-[10px] font-mono text-emerald-300/90 whitespace-pre-wrap leading-relaxed">
                      {st.logs.join('\n')}
                    </pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
